import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ArrowRight, Sparkles, Camera, Users, Zap } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'
import LoginDialog from '../components/auth/LoginDialog'
import RegisterDialog from '../components/auth/RegisterDialog'

const Landing: React.FC = () => {
  const [showLogin, setShowLogin] = useState(false)
  const [showRegister, setShowRegister] = useState(false)

  const handleLoginClick = () => {
    setShowLogin(true)
  }

  const handleRegisterClick = () => {
    setShowRegister(true)
  }

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Fullscreen Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/landing-bg.jpg)' }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-primary-900/60 backdrop-blur-sm" />
      </div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between p-6">
          <div className="flex items-center">
            <img src="/logotype.svg" alt="StoryShot" className="h-16 w-auto" />
          </div>
          
          <div className="flex items-center space-x-4">
            <button
              onClick={handleLoginClick}
              className="glass-button font-ui font-normal"
            >
              Sign In
            </button>
            <button
              onClick={handleRegisterClick}
              className="glass-button bg-primary-500/30 hover:bg-primary-500/40 font-ui font-normal"
            >
              Get Started
            </button>
          </div>
        </header>

        {/* Hero Section */}
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-headline text-white mb-6 leading-tight">
                Visual Storytelling
                <br />
                <span className="gradient-text">Made Simple</span>
              </h1>
              
              <p className="text-xl md:text-2xl font-body font-light text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
                Create stunning storyboards, organize your visual projects, and collaborate seamlessly with our intuitive glass-morphism interface.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6 mb-12">
                <button
                  onClick={handleRegisterClick}
                  className="glass-button bg-primary-500/30 hover:bg-primary-500/40 px-8 py-4 text-lg font-ui font-normal flex items-center space-x-2 group"
                >
                  <span>Start Creating</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button
                  onClick={handleLoginClick}
                  className="glass-button px-8 py-4 text-lg font-ui font-normal"
                >
                  Sign In to Continue
                </button>
              </div>
            </motion.div>

            {/* Features Grid */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
            >
              <div className="glass-card text-center">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Camera className="w-8 h-8 text-primary-300" />
                </div>
                <h3 className="text-xl font-headline text-white mb-2">Visual Projects</h3>
                <p className="font-body font-light text-white/70">
                  Organize your storyboards with scenes and images in a beautiful grid layout.
                </p>
              </div>

              <div className="glass-card text-center">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary-300" />
                </div>
                <h3 className="text-xl font-headline text-white mb-2">Team Collaboration</h3>
                <p className="font-body font-light text-white/70">
                  Share projects with your team and collaborate on visual storytelling together.
                </p>
              </div>

              <div className="glass-card text-center">
                <div className="w-16 h-16 bg-primary-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Zap className="w-8 h-8 text-primary-300" />
                </div>
                <h3 className="text-xl font-headline text-white mb-2">Lightning Fast</h3>
                <p className="font-body font-light text-white/70">
                  Built with modern technology for smooth, responsive performance.
                </p>
              </div>
            </motion.div>
          </div>
        </main>

        {/* Footer */}
        <footer className="p-6 text-center">
          <p className="font-body font-light text-white/50">
            © {new Date().getFullYear()} StoryShot. Crafted with ❤️ by{' '}
            <a 
              href="https://mesh.design" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white/50 hover:text-white/70 transition-colors"
            >
              MESH
            </a>
            {' '}for visual storytellers.
          </p>
        </footer>
      </div>

      {/* Dialogs */}
      <LoginDialog open={showLogin} onClose={() => setShowLogin(false)} />
      <RegisterDialog open={showRegister} onClose={() => setShowRegister(false)} />
    </div>
  )
}

export default Landing 